/**
 * Belt color model aligned with Android com.neweracss.design.model.Belt
 */

const COLORS = {
  white: "#F5F5F5",
  grey: "#7E8284",
  yellow: "#FAC840",
  orange: "#F15E3A",
  green: "#105D36",
  blue: "#2969AE",
  purple: "#6C3C90",
  brown: "#4F3319",
  black: "#1A1A1A",
};

/** @typedef {{ color: string, stripeColor?: string, isCoral?: boolean, token: string }} BeltModel */

/** @type {Record<string, Omit<BeltModel, "token">>} */
const BELT_MAP = {
  white: { color: COLORS.white },
  greywhite: { color: COLORS.grey, stripeColor: COLORS.white },
  grey: { color: COLORS.grey },
  greyblack: { color: COLORS.grey, stripeColor: COLORS.black },
  yellowwhite: { color: COLORS.yellow, stripeColor: COLORS.white },
  yellow: { color: COLORS.yellow },
  yellowblack: { color: COLORS.yellow, stripeColor: COLORS.black },
  orangewhite: { color: COLORS.orange, stripeColor: COLORS.white },
  orange: { color: COLORS.orange },
  orangeblack: { color: COLORS.orange, stripeColor: COLORS.black },
  greenwhite: { color: COLORS.green, stripeColor: COLORS.white },
  green: { color: COLORS.green },
  greenblack: { color: COLORS.green, stripeColor: COLORS.black },
  blue: { color: COLORS.blue },
  purple: { color: COLORS.purple },
  brown: { color: COLORS.brown },
  black: { color: COLORS.black },
  coral: { color: "#E31E24", isCoral: true },
};

export function normalizeBeltKey(belt) {
  if (!belt || typeof belt !== "string") return "";
  return belt
    .toLowerCase()
    .replace(/[-_/]/g, "")
    .replace(/gray/g, "grey")
    .trim();
}

/** @param {string} belt @returns {BeltModel|null} */
export function resolveBeltModel(belt) {
  const token = normalizeBeltKey(belt);
  if (!token) return null;

  const def = BELT_MAP[token];
  if (def) return { ...def, token };

  // Fallback: try matching a solid color prefix
  for (const key of Object.keys(COLORS)) {
    if (token === key || token.startsWith(key)) {
      return { color: COLORS[key], token: key };
    }
  }

  return { color: "#9CA3AF", token };
}

export function getBeltModelAccent(belt) {
  const model = resolveBeltModel(belt);
  if (!model) return "#374151";
  if (model.isCoral) return "#E31E24";
  if (model.stripeColor && model.stripeColor !== COLORS.white && model.stripeColor !== COLORS.black) {
    return model.color;
  }
  return model.color;
}

export function isBlackBelt(belt) {
  return normalizeBeltKey(belt) === "black";
}

export function isCoralBelt(belt) {
  return normalizeBeltKey(belt) === "coral";
}

/** Black / coral BJJ belts use the rank sleeve and degree stripes in BeltView. */
export function beltSupportsDegreeStripes(belt) {
  const token = normalizeBeltKey(belt);
  return token === "black" || token === "coral";
}

export function stitchingColor(belt) {
  const token = normalizeBeltKey(belt);
  if (token === "white") return "rgba(156, 163, 175, 0.6)";
  if (token === "black") return "rgba(156, 163, 175, 0.15)";
  return "rgba(0, 0, 0, 0.1)";
}

export function sleeveColor(belt) {
  if (isBlackBelt(belt) || isCoralBelt(belt)) return "#D32F2F";
  return "#000000";
}
